import React, { useState, useCallback } from 'react';
import {
  Box, Tabs, Tab, Button, IconButton, Chip, Dialog,
  DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Switch, FormControlLabel,
  CircularProgress, Typography,
} from '@mui/material';
import { DataGrid } from '../common/datagrid/ResponsiveDataGrid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSnackbar } from 'notistack';
import * as codeApi from '../../lib/api/commonCode';
import CodeFormContainer from '../../containers/codemng/CodeFormContainer';
import CodeListContainer from '../../containers/codemng/CodeListContainer';
import LinkCodeTabPanel from './LinkCodeTabPanel';

// ── 코드 그룹 정의 ──────────────────────────────────────────────
const CODE_GROUPS = [
  {
    blcCd: 'EQP_TYP',
    label: '장비 분류',
    desc: '장비 등록 시 사용하는 장비 종류 분류 코드',
    hasColor: false,
  },
  {
    blcCd: 'EQP_LOG_TYP',
    label: '운영 유형',
    desc: '운영이력 입력 시 가동/고장/수리 등 유형 코드 (REV_VAL_1 = Chip 색상)',
    hasColor: true,
  },
  {
    blcCd: 'EQP_STATUS',
    label: '장비 상태',
    desc: '장비의 현재 운영 상태 코드 (REV_VAL_1 = Chip 색상)',
    hasColor: true,
  },
];

const AUTH_GROUP = {
  blcCd: 'AUTH',
  mlcCd: 'USR',
  label: '권한',
  desc: '사용자 권한 코드 (사원 등록 시 권한 체크박스에 표시)',
  hasColor: false,
};

const COLOR_OPTIONS = ['success', 'error', 'warning', 'info', 'default', 'secondary'];

const EMPTY_FORM = {
  slcCd: '',
  itemNm: '',
  itemDesc: '',
  revVal1: 'default',
  step: '1',
  useYn: 'Y',
};

// ── 코드 탭 패널 (공통) ──────────────────────────────────────────
const CodeTabPanel = ({ group }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { blcCd: group.blcCd };
      if (group.mlcCd) params.mlcCd = group.mlcCd;
      const res = await codeApi.getComCodes(params);
      const list = res.data?.list || res.data || [];
      setRows(list.map((r, i) => ({ id: r.commonUuid || i, ...r })));
    } catch {
      enqueueSnackbar('코드 목록 조회 실패', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [group.blcCd, group.mlcCd, enqueueSnackbar]);

  React.useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      slcCd: row.slcCd || '',
      itemNm: row.itemNm || '',
      itemDesc: row.itemDesc || '',
      revVal1: row.revVal1 || 'default',
      step: row.step || '1',
      useYn: row.useYn || 'Y',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.slcCd.trim() || !form.itemNm.trim()) {
      enqueueSnackbar('코드값과 코드명은 필수입니다.', { variant: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        blcCd: group.blcCd,
        mlcCd: group.mlcCd || '',
        slcCd: form.slcCd.trim().toUpperCase(),
        itemNm: form.itemNm.trim(),
        itemDesc: form.itemDesc.trim(),
        revVal1: group.hasColor ? form.revVal1 : '',
        step: form.step,
        useYn: form.useYn,
      };
      if (editing) {
        await codeApi.editCode(editing.commonUuid || editing.id, payload);
        enqueueSnackbar('수정되었습니다.', { variant: 'success' });
      } else {
        await codeApi.addComCode(payload);
        enqueueSnackbar('추가되었습니다.', { variant: 'success' });
      }
      setDialogOpen(false);
      load();
    } catch {
      enqueueSnackbar('저장에 실패했습니다.', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`"${row.itemNm}" 코드를 삭제하시겠습니까?`)) return;
    try {
      await codeApi.removeCode(row.commonUuid || row.id);
      enqueueSnackbar('삭제되었습니다.', { variant: 'success' });
      load();
    } catch {
      enqueueSnackbar('삭제에 실패했습니다.', { variant: 'error' });
    }
  };

  const columns = [
    { field: 'slcCd', headerName: '코드값', width: 120 },
    { field: 'itemNm', headerName: '코드명', width: 130 },
    { field: 'itemDesc', headerName: '설명', flex: 1, minWidth: 160 },
    ...(group.hasColor ? [{
      field: 'revVal1', headerName: '색상', width: 110,
      renderCell: ({ value }) => value
        ? <Chip label={value} color={value} size="small" />
        : null,
    }] : []),
    { field: 'step', headerName: '정렬', width: 70 },
    {
      field: 'useYn', headerName: '사용', width: 70,
      renderCell: ({ value }) => (
        <Chip label={value === 'Y' ? '사용' : '미사용'}
          color={value === 'Y' ? 'success' : 'default'} size="small" />
      ),
    },
    {
      field: '_actions', headerName: '', width: 90, sortable: false,
      renderCell: ({ row }) => (
        <Box>
          <IconButton size="small" onClick={() => openEdit(row)}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => handleDelete(row)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary">{group.desc}</Typography>
        <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
          코드 추가
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <DataGrid
          rows={rows}
          columns={columns}
          autoHeight
          disableColumnMenu
          density="compact"
          pageSizeOptions={[20, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 20 } } }}
        />
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? '코드 수정' : '코드 추가'} — {group.label}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="코드값 (영문 대문자)"
              value={form.slcCd}
              onChange={(e) => setForm(f => ({ ...f, slcCd: e.target.value }))}
              disabled={!!editing}
              size="small"
              helperText="예: PRESS, CNC (수정 불가)"
            />
            <TextField
              label="코드명"
              value={form.itemNm}
              onChange={(e) => setForm(f => ({ ...f, itemNm: e.target.value }))}
              size="small"
              required
            />
            <TextField
              label="설명"
              value={form.itemDesc}
              onChange={(e) => setForm(f => ({ ...f, itemDesc: e.target.value }))}
              size="small"
            />
            {group.hasColor && (
              <TextField
                select label="색상 (Chip)"
                value={form.revVal1}
                onChange={(e) => setForm(f => ({ ...f, revVal1: e.target.value }))}
                size="small"
              >
                {COLOR_OPTIONS.map((c) => (
                  <MenuItem key={c} value={c}>
                    <Chip label={c} color={c} size="small" sx={{ mr: 1 }} /> {c}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              label="정렬순서"
              value={form.step}
              onChange={(e) => setForm(f => ({ ...f, step: e.target.value }))}
              size="small"
              type="number"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.useYn === 'Y'}
                  onChange={(e) => setForm(f => ({ ...f, useYn: e.target.checked ? 'Y' : 'N' }))}
                />
              }
              label="사용 여부"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>취소</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : '저장'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ── 메인 템플릿 ─────────────────────────────────────────────────
const CodeMngTemplate = () => {
  const [tab, setTab] = useState(0);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setData(null);
  };

  const editFunction = (params) => {
    setData(params);
    setOpen(true);
  };

  // 탭 인덱스: 0=공통코드, 1~3=설비코드, 4=권한 코드, 5=바로가기 URL
  const authTabIndex = CODE_GROUPS.length + 1;
  const linkTabIndex = authTabIndex + 1;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', m: 1 }}>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="공통코드" />
        {CODE_GROUPS.map((g) => (
          <Tab key={g.blcCd} label={g.label} />
        ))}
        <Tab label="권한 코드" />
        <Tab label="바로가기 URL" />
      </Tabs>

      {tab === 0 && (
        <>
          <CodeListContainer editFunction={editFunction} handleAdd={handleOpen} />
          <CodeFormContainer
            type={data != null ? 'edit' : 'add'}
            data={data}
            open={open}
            handleClose={handleClose}
          />
        </>
      )}

      {CODE_GROUPS.map((g, i) => (
        tab === i + 1 && <CodeTabPanel key={g.blcCd} group={g} />
      ))}

      {tab === authTabIndex && <CodeTabPanel group={AUTH_GROUP} />}

      {tab === linkTabIndex && <LinkCodeTabPanel />}
    </Box>
  );
};

export default CodeMngTemplate;
